import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { FavoriteControl } from "@/Common/Components/Favorites/FavoriteControl";
import { InfoIconControl } from "@/Common/Components/IconControls/InfoIconControl";
import { VigilanceIcon } from "@/Common/Components/IconControls/VigilanceIcon";
import { CommonHelper } from "@/Common/Helpers/CommonHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { PatientMedicationSearchDetails } from "@/Patient/Components/Medication/PatientMedicationSearchDetails";
import { MedicationCoverage } from "@/Patient/Models/Medication/Enums/Medication";
import { MedicationTerm } from "@/Patient/Models/Medication/Enums/MedicationTerm";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { GetLimitedUseCodesResponse, MedicationCoverages, MedicationResponse } from "@/Patient/Models/Medication/ResponseModels/MedicationResponse";
import { LongTermMedicationIcon } from "./LongTermMedicationIcon";

import styles from "./SearchMedication.module.scss";

interface IMedicationsSearchAutoComplete {
    onItemSelect: (item: MedicationSearchListItem) => void;
    onSearchBoxClick?: () => void;
    onBlur?: (customMedicationName: string) => void;

    debounce: number;

    newValue?: string;
    newValueEventId?: string;

    disableFavorites?: boolean;
    disableIndicator?: boolean;
    width?: string;
    className?: string;

    isInvalid?: boolean;

    encounterSiteId?: string;

    isInputDisabled?: boolean;

    dataCy?: string;
}

class MedicationsSearchAutoCompleteState {
    public id: string;
    public loadedMedicationResponseDetails: MedicationResponse;
    public showResultList: boolean;
    public sNewValueEventId: string;
    public text: string;
    public list: Immutable.List<MedicationSearchListItem>;
    public favoritePrescriptions: Immutable.List<MedicationModel>;
    public limitedUseCodes: string[];

    constructor(init: Partial<MedicationsSearchAutoCompleteState>) {
        Object.assign(this, init);
    }

    public static default(): MedicationsSearchAutoCompleteState {
        return new MedicationsSearchAutoCompleteState({
            showResultList: false,
            sNewValueEventId: "",
            text: "",
            list: Immutable.List([]),
            loadedMedicationResponseDetails: null,
            id: "medicationSearchField" + sessionStore.newId(),
            favoritePrescriptions: Immutable.List(CommonHelper.getCloneDeep(cacheStore.FavoritePrescriptions.toArray())) ?? Immutable.List([]),
            limitedUseCodes: []
        });
    }
}

class MedicationSearchListItem {
    public isFavorite: boolean;
    public isActive: boolean;
    public responseItem?: MedicationResponse;
    public favoriteItem: MedicationModel[];

    constructor(init?: Partial<MedicationSearchListItem>) {
        Object.assign(this, init);
    }
}

class SearchMedication extends BaseReactComponent<IMedicationsSearchAutoComplete, MedicationsSearchAutoCompleteState> {
    private timeoutReferenceSearchMed: number;
    private timeoutReferenceBlur: number;
    private timeoutReferenceBlurReset: number;
    private reloadFavoritesOnBlur: boolean;
    private isBlurEnabled: boolean;
    private orderedResponseId: number;
    private lastResponseId: number;
    private containerRef: HTMLDivElement;

    constructor(props: IMedicationsSearchAutoComplete) {
        super(props);
        this.isBlurEnabled = true;
        this.orderedResponseId = 0;
        this.lastResponseId = 0;
        this.timeoutReferenceSearchMed = -1;
        this.timeoutReferenceBlur = -1;
        this.timeoutReferenceBlurReset = -1;
        this.state = MedicationsSearchAutoCompleteState.default();
        this.handleClickOutside = this.handleClickOutside.bind(this);
    }

    private handleClickOutside(event: MouseEvent): void {
        if (this.state.list.count() > 0 && this.state.showResultList)
            if (this.containerRef?.contains?.(event.target as HTMLElement) === false) {
                if (this.state.loadedMedicationResponseDetails)
                    this.mutateState(s => s.loadedMedicationResponseDetails = null);
                else
                    this.forceClose();
            }
    }

    private getMedicationCoverageProvinceCode(): string {
        const isCanadianProvince = (stateId: string): boolean => cacheStore.GetCountryForRegion(stateId)?.LookupCode === "CNCANADA";

        const healthCardProvinceIds = patientStore.getActivePatientHealthCards()
            ?.map(p => p.ProvinceLookupId)
            .filter(id => isCanadianProvince(id)) ?? [];

        let siteProvinceId = cacheStore.GetSiteById(this.props.encounterSiteId)
            ?.Model
            .Adresses
            .firstOrDefault(p => isCanadianProvince(p.StateLookupId))?.StateLookupId;
        if (!siteProvinceId) {
            siteProvinceId = sessionStore.getCurrentEmployee().getActiveSite()
                ?.Model
                .Adresses
                .firstOrDefault(p => isCanadianProvince(p.StateLookupId))?.StateLookupId;
        }

        const provinceId =
            healthCardProvinceIds?.firstOrDefault(id => id === siteProvinceId)
            ?? healthCardProvinceIds?.firstOrDefault()
            ?? siteProvinceId;

        return cacheStore.GetStateCodeFromStateLookupId(provinceId);
    }

    public render(): JSX.Element {
        return (
            <div
                ref={ref => this.containerRef = ref}
                className={this.props.className}
                style={{ position: "relative" }}
                data-cy={this.props.dataCy}
            >
                <div
                    className={`search-field ${this.props.isInvalid ? "invalid" : ""}`}
                    style={{ width: this.props.width ?? "340px", "padding": "6px" }}
                >
                    <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", width: "100%" }}>
                        <div style={{ display: "inline-block", color: "#aaa", height: "15px" }}>
                            <span className="glyphicon glyphicon-search" />
                        </div>
                        <input
                            id={this.state.id}
                            key={this.state.id}
                            onClick={e => this.onClick(e)}
                            onBlur={() => this.onBlur()}
                            onFocus={() => this.onFocus()}
                            type="text"
                            onChange={e => this.onTextChange(e.target.value)}
                            onKeyDown={e => this.inputKeyDown(e)}
                            value={this.state.text}
                            className="form-control"
                            style={{ height: "21px", display: "inline-block", marginLeft: "5px", fontWeight: "normal" }}
                            disabled={!!this.props.isInputDisabled}
                        />
                    </div>
                </div>
                <div
                    className="list-container-fmp"
                    onMouseEnter={() => this.onMouseEnter()}
                    onTouchStart={() => this.onTouchStart()}
                    style={{ position: "absolute", left: "2px", top: "34px", zIndex: 1 }}
                >
                    {this.state.list.size > 0 && this.state.showResultList && (
                        <div className="results-container" style={{ width: "340px", backgroundColor: "#fff", border: "solid 1px #ccc" }} onClick={e => this.noPropagation(e)}>
                            <ul className="results-list" style={{ overflowY: "scroll", overflowX: "hidden", maxHeight: "400px" }}>
                                {this.state.list.map((listItem, i) => listItem.favoriteItem
                                    ? this.renderFavorite(listItem, i)
                                    : this.renderMedicationResponse(listItem, this.getMedicationCoverageProvinceCode())
                                )}
                            </ul>
                        </div>
                    )}
                    {this.state.list.size > 0 && this.state.showResultList && this.state.loadedMedicationResponseDetails && (
                        <div
                            className="popup"
                            style={{
                                position: "absolute",
                                top: "0",
                                left: "340px",
                                height: "400px",
                                width: "500px",
                                overflowY: "auto"
                            }}
                            onClick={e => {
                                e.stopPropagation();
                                this.returnFocus();
                            }}
                        >
                            <PatientMedicationSearchDetails
                                referenceId="_currentMedDetail"
                                model={this.state.loadedMedicationResponseDetails}
                                onExceptionClick={exception => this.handleOnExceptionClick(exception)}
                                encounterSiteId={this.props.encounterSiteId}
                                onSearchByActiveIngredientClick={(drugIdentificationNumberOrName: string) =>
                                    this.onTextChange(drugIdentificationNumberOrName)
                                }
                                onLimitedUseCodeClick={code => this.handleOnExceptionClick(code)}
                                limitedUseCodes={this.state.limitedUseCodes}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    private handleOnExceptionClick(codeException: string): void {
        if (!this.state.loadedMedicationResponseDetails)
            return;

        const item = this.state.list.toArray().firstOrDefault(x => !x.isFavorite && x.responseItem?.Id == this.state.loadedMedicationResponseDetails.Id);
        if (!item)
            return;

        item.responseItem.Instruction = "EXC: " + codeException;

        this.selectItem(item);
    }

    public componentDidMount(): void {
        this.returnFocus();
        document.addEventListener("click", this.handleClickOutside);
        if (!this.props.disableFavorites)
            window.setTimeout(() => this.loadData(""), 50);
        else
            this.checkForAutoSearchTriggers();
    }

    public componentWillUnmount(): void {
        this.isBlurEnabled = false;
        document.removeEventListener("click", this.handleClickOutside);
    }

    public componentDidUpdate(): void {
        this.checkForAutoSearchTriggers();
    }

    public checkForAutoSearchTriggers(): void {
        // External Search Trigger
        if (this.props.newValueEventId != this.state.sNewValueEventId && this.props.newValue != "" && this.props.newValue != null) {
            this.timeoutReferenceSearchMed = window.setTimeout(() => {
                this.returnFocus();
                this.mutateState(s => {
                    s.showResultList = true;
                    s.text = this.props.newValue;
                    s.sNewValueEventId = this.props.newValueEventId;
                });

                if (this.props.newValueEventId)
                    this.loadData(this.props.newValue, true);
            }, 50);
        }
    }

    public renderMedicationResponse(listItem: MedicationSearchListItem, coverageProvinceCode: string): JSX.Element {
        const itemMed = listItem.responseItem;

        const safeStrength = itemMed.Strength != null ? itemMed.Strength : "";
        const medicationName = itemMed.Name + " " + safeStrength;
        const form = itemMed.Form;

        const divStyle: React.CSSProperties = {
            height: "45px",
            justifyContent: "space-between",
            borderBottom: "solid 1px #ccc",
            backgroundColor: listItem.isActive ? "#e0e0f7" : null
        };

        return (
            <li key={itemMed.Id} style={{ height: "45px" }}>
                <div
                    className="flex-container"
                    style={divStyle}
                    onKeyDown={e => this.inputKeyDown(e)}
                    onClick={() => this.selectItem(listItem)}
                >
                    <div className={styles.searchResultDataContainer}>
                        <div className={styles.searchResultNameContainer}>
                            <LongTermMedicationIcon visible={cacheStore.ClinicSettings.EnableLongTermMedication && itemMed.Term === MedicationTerm.LongTerm} />
                            <span style={{ color: "#0070d2" }}>{medicationName}</span>
                        </div>
                        <div className={styles.searchResultForm}>
                            <LongTermMedicationIcon visible={false} /> {/* dummy */}
                            <span>{form}</span>
                        </div>
                    </div>

                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                        {itemMed.HasMonograph && settingsStore.configuration.get("PharmacoTherapeuticActive") && (
                            <VigilanceIcon
                                id={itemMed.Id + "_handle_mono"}
                                styles={{ marginTop: "5px", marginRight: "2px" }}
                                height="31px"
                                onClick={() => this.getMonograph(itemMed)}
                            />
                        )}
                        {!this.props.disableIndicator &&
                            this.renderIconButton(itemMed, coverageProvinceCode)
                        }
                    </div>
                </div>

            </li>
        );
    }

    private renderIconButton(itemMed: MedicationResponse, provCode: string): JSX.Element {
        const coverageCode = MedicationCoverages.getCoverageByProvinceCode(provCode, itemMed.MedicationCoverages);
        const isPartialCoverage =
            coverageCode !== MedicationCoverage.NotCovered
            && coverageCode !== MedicationCoverage.Covered;

        return (
            <InfoIconControl
                isException={isPartialCoverage}
                onClick={() => this.loadMedicationDetails(itemMed)}
                imgProps={{
                    style: { height: "36px", marginTop: "3px" }
                }}
            />
        );
    }

    public renderFavorite(listItem: MedicationSearchListItem, i: number): JSX.Element {
        const items = [...listItem.favoriteItem].sortByProperty(a => a.EMRTemplateSortIndex);
        const minHeight = 45 * items.length;

        const medicationRender = items.map((item, index) => {
            if (item.Model?.CreatorEmployeeId) { // Hidden Fav
                let divStyle: React.CSSProperties = { minHeight: "45px", justifyContent: "space-between" };
                if (listItem.isActive)
                    divStyle.backgroundColor = "#e0e0f7";

                if (index == items.length - 1)
                    divStyle.borderBottom = "solid 1px #ccc";

                const dosages = item.GetDosagesDisplay({ includeRenewalsOnLastDosage: false });
                const dosageRender = dosages.map((x, idx) => <div key={index + "_" + idx}>{x}</div>);

                return (
                    <div
                        key={item.Model.Id}
                        className="flex-container"
                        style={divStyle}
                        onKeyDown={e => this.inputKeyDown(e)}
                        onClick={() => this.selectItem(listItem)}
                    >
                        <div
                            style={{
                                margin: "2px 0px 0px 5px",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis"
                            }}
                        >
                            <LongTermMedicationIcon
                                visible={cacheStore.ClinicSettings.EnableLongTermMedication && item.isLongTerm()}
                                className={styles.longTermIcon}
                            />
                            <span className={styles.favoriteMedicationName}>{item.GetName(false)}</span>
                            <div className={styles.dosageContainer}>
                                <span>{dosageRender}</span>
                            </div>
                        </div>

                        {index == 0 && (
                            <div className={styles.iconContainer}>
                                <FavoriteControl
                                    imageStyles={{ height: "22px", verticalAlign: "baseline" }}
                                    isFavoriteSet={item.Model.CreatorEmployeeId !== GuidHelper.Empty()}
                                    onChange={isFavorite => isFavorite
                                        ? this.addFavorite(item.Model.EMRTemplatePrescriptionId)
                                        : this.removeFavorite(item.Model.EMRTemplatePrescriptionId)
                                    }
                                />
                            </div>
                        )}
                    </div>
                );
            }
        });

        return (
            <li key={i + "__code_"} style={{ minHeight: `${minHeight}px` }}>
                {medicationRender}
            </li>
        );
    }

    private onTextChange(value: string): void {
        this.mutateState(s => {
            s.showResultList = true;
            s.text = value;
        });

        if (this.timeoutReferenceSearchMed !== -1) {
            clearTimeout(this.timeoutReferenceSearchMed);
            this.timeoutReferenceSearchMed = -1;
        }

        this.loadData(value);
    }

    private setForceHide(show: boolean = true): void {
        this.mutateState(s => s.showResultList = show);
    }

    private inputKeyDown(e: React.KeyboardEvent): void {
        // up
        if (e.keyCode === 38) {
            e.preventDefault();
            this.moveSelectionUpOrDown(true);
        }

        // down
        if (e.keyCode === 40) {
            e.preventDefault();
            this.moveSelectionUpOrDown(false);
        }

        // enter
        if (e.keyCode === 13) {
            if (!this.state.showResultList || !this.state.list?.count()) {
                this.setForceHide(true);
                this.loadData(this.state.text);
            }
            else
                this.addSelectedItem();
        }

        // escape
        if (e.keyCode === 27)
            this.setForceHide(false);
    }

    // keyboard triggered
    private moveSelectionUpOrDown(isDirectionUp: boolean): void {
        const collection = this.state.list.toArray();
        if (collection.length == 0)
            return;

        let idx = this.indexOfSelectedItem();
        if (idx === -1) {
            collection.forEach(x => x.isActive = false);
            collection[isDirectionUp ? collection.length - 1 : 0].isActive = true;
            this.updateStateWithNewMedicationList(collection);
            return;
        }

        if ((idx === 0 && isDirectionUp) || (idx === collection.length - 1 && !isDirectionUp)) {
            collection[isDirectionUp ? 0 : collection.length - 1].isActive = false;
            this.updateStateWithNewMedicationList(collection);
            return;
        }

        collection[idx].isActive = false;
        idx = idx + (isDirectionUp ? -1 : 1);

        collection[idx].isActive = true;
        this.updateStateWithNewMedicationList(collection);
    }

    private indexOfSelectedItem(): number {
        return this.state.list.findIndex(x => x.isActive);
    }

    private updateStateWithNewMedicationList(list: MedicationSearchListItem[]): void {
        this.mutateState(s => {
            s.showResultList = true;
            s.list = Immutable.List(list);
        });
    }

    private addSelectedItem(): void {
        const item = this.state.list.filter(x => x.isActive == true).toArray();
        if (item.length > 0)
            this.selectItem(item[0]);
    }

    private loadData(text: string, noDebounce: boolean = false): void {
        this.timeoutReferenceSearchMed = window.setTimeout(
            () => {
                this.mutateState(s => s.loadedMedicationResponseDetails = null);
                this.loadDataRaw(text);
            },
            noDebounce ? 50 : this.props.debounce
        );
    }

    private async loadDataRaw(text: string): Promise<void> {
        let dictionary = sessionStore.getMedicationSourceKey();

        if (text.trim().length >= 2) {
            this.orderedResponseId = this.orderedResponseId + 1;

            const response = await Client.postAsync<{ OrderedResponseId: number, SearchMedicationResults: MedicationResponse[] }>(
                ServerOperations.Server.MedfarDictionary_SearchMedications,
                {
                    SearchString: text,
                    Locale: sessionStore.locale,
                    OrderedResponseId: this.orderedResponseId,
                    SpecificDictionary: dictionary
                }
            );

            if (!response.isSuccess) {
                console.error(response.errors);
                return;
            }

            if (response.data.OrderedResponseId >= this.lastResponseId) {
                this.lastResponseId = response.data.OrderedResponseId;

                let listItems = response.data.SearchMedicationResults
                    .map(model => new MedicationSearchListItem({ isFavorite: false, responseItem: model }));

                // Favorites on top
                listItems = [...this.getFavorites(text), ...listItems];

                if (listItems?.length)
                    listItems[0].isActive = true;

                this.mutateState(s => s.list = Immutable.List(listItems));
            }
        }
        else {
            const list = text.length
                ? this.getFavorites(text)
                : [];
            this.mutateState(s => s.list = Immutable.List(list));
        }
    }

    public getFavorites(searchText: string): MedicationSearchListItem[] {
        if (this.props.disableFavorites == true)
            return [];

        let favorites = [];
        if (!!this.state.favoritePrescriptions) {
            let tmpFavorites: MedicationModel[] = [];
            if (!!searchText?.trim()) {
                const containsOptions = { ignoreCase: true, ignoreDiacritics: true };
                const splittedSearch = searchText.split(" ");

                tmpFavorites = this.state.favoritePrescriptions
                    .filter(x => {
                        const medicationNameClean = x.GetName(true);

                        let conditions = [
                            () => false,
                            () => true,
                            () => true
                        ];
                        if (splittedSearch?.length > 0)
                            conditions[0] = () => medicationNameClean.split(" ").some(word => word.contains(splittedSearch[0], containsOptions));

                        if (splittedSearch?.length > 1)
                            conditions[1] = () => medicationNameClean.contains(splittedSearch[1], containsOptions);

                        if (splittedSearch?.length > 2)
                            conditions[2] = () => medicationNameClean.contains(splittedSearch[2], containsOptions);

                        return conditions.every(condition => condition());
                    })
                    .toArray();
            }
            else
                tmpFavorites = this.state.favoritePrescriptions.toArray();

            const uniquePrescriptionIds = Object
                .keys(tmpFavorites
                    .map(p => p.Model.EMRTemplatePrescriptionId)
                    .filter(id => !!id)
                    .toDictionary(id => id)
                );

            for (let _prescriptionId of uniquePrescriptionIds) {
                const items = this.state.favoritePrescriptions.filter(p => p.Model.EMRTemplatePrescriptionId == _prescriptionId);

                favorites.push(new MedicationSearchListItem({
                    isFavorite: true,
                    favoriteItem: items.toArray()
                }));
            }
        }

        return favorites;
    }

    public selectItem(item: MedicationSearchListItem): void {
        this.mutateState(s => {
            s.text = "";
            s.showResultList = false;
            s.list = Immutable.List(this.getFavorites("").reverse());
            s.loadedMedicationResponseDetails = null;
        });

        this.props.onItemSelect(item);

        window.setTimeout(
            () => {
                this.returnFocus();
                $("#" + this.state.id).select();
            },
            150
        );
    }

    public onFocus(): void {
        clearTimeout(this.timeoutReferenceBlur);

        window.setTimeout(
            () => $("#" + this.state.id).select(),
            50
        );
    }

    public async loadMedicationDetails(item: MedicationResponse): Promise<void> {
        if (item.Id !== this.state.loadedMedicationResponseDetails?.Id) {
            const limitedUseCodes = await this.fetchVigilanceProductExceptionCodes(item.Din);
            this.mutateState(s => {
                s.loadedMedicationResponseDetails = item;
                s.limitedUseCodes = limitedUseCodes;
            });
        }
        else {
            this.mutateState(s => s.loadedMedicationResponseDetails = null);
        }

        this.returnFocus();
    }

    public async fetchVigilanceProductExceptionCodes(din: string): Promise<string[]> {
        const response = await Client.postAsync<GetLimitedUseCodesResponse>(
            ServerOperations.Server.MedfarDictionary_GetVigilanceProductLimitedUseCodes,
            {
                Din: din
            }
        );

        return response.isSuccess ? response.data.LimitedUseCodes : [];
    }

    public getMonograph(item: MedicationResponse): void {
        sessionStore.dispatch(
            LocalPatientOperations.PrescriptionTab.MedicationDetails_GetVigilanceMonographie,
            {
                item: item.CodeGen,
                id: item.Id
            }
        );

        this.returnFocus();
    }

    public async removeFavorite(PrescriptionId: string): Promise<void> {
        await cacheStore.requestToggleFavorite(PrescriptionId, true, false);
        this.reloadFavoritesOnBlur = true;
        this.returnFocus();
        this.syncCacheStoreFavoritePrescriptions();
    }

    public async addFavorite(PrescriptionId: string): Promise<void> {
        await cacheStore.requestToggleFavorite(PrescriptionId, false, false);
        this.returnFocus();
        this.syncCacheStoreFavoritePrescriptions();
    }

    public onClick(e: React.MouseEvent<HTMLInputElement>): void {
        if (!this.state.showResultList)
            this.mutateState(s => {
                s.showResultList = true;
                s.list = this.refreshFavoritesInCurrentList();
            });

        if (this.props.onSearchBoxClick != null)
            window.setTimeout(() => this.props.onSearchBoxClick(), 50);

        e.stopPropagation();
    }

    public refreshFavoritesInCurrentList(): Immutable.List<MedicationSearchListItem> {
        const currentList = this.state.list
            .filter(p => p.isFavorite != true)
            .toArray();

        return [
            ...this.getFavorites(this.state.text),
            ...currentList
        ].toImmutable();
    }

    public forceClose(): void {
        this.mutateState(s => {
            s.showResultList = false;
            s.loadedMedicationResponseDetails = null;
        });

        if (this.reloadFavoritesOnBlur) {
            this.reloadFavoritesOnBlur = false;
            this.reloadFavoritePrescriptions();
        }

        this.props.onBlur?.(this.state.text);
    }

    public onBlur(): void {
        if (this.timeoutReferenceBlur !== -1) {
            clearTimeout(this.timeoutReferenceBlur);
            this.timeoutReferenceBlur = -1;
        }

        this.timeoutReferenceBlur = window.setTimeout(() => {
            if (this.isBlurEnabled) {
                this.mutateState(s => s.showResultList = false);

                if (this.reloadFavoritesOnBlur) {
                    this.reloadFavoritesOnBlur = false;
                    this.reloadFavoritePrescriptions();
                }

                this.props.onBlur?.(this.state.text);
            }
        }, 150);
    }

    public onMouseEnter(): void {
        this.isBlurEnabled = false;
    }

    public onTouchStart(): void {
        clearTimeout(this.timeoutReferenceBlurReset);

        this.timeoutReferenceBlurReset = window.setTimeout(
            () => this.isBlurEnabled = true,
            300
        );

        this.isBlurEnabled = false;
    }

    public returnFocus(): void {
        $("#" + this.state.id).focus();
    }

    public noPropagation(e: React.MouseEvent<HTMLDivElement>): void {
        e.stopPropagation();
    }

    private async reloadFavoritePrescriptions(): Promise<void> {
        await cacheStore.requestReloadFavoritePrescription();
        this.syncCacheStoreFavoritePrescriptions();
    }

    private syncCacheStoreFavoritePrescriptions(): void {
        this.mutateState(p => p.favoritePrescriptions = Immutable.List(CommonHelper.getCloneDeep(cacheStore.FavoritePrescriptions.toArray())), () => {
            this.mutateState(p => p.list = this.refreshFavoritesInCurrentList());
        });
    }
}

export { MedicationSearchListItem, SearchMedication };
